import React, { useState, useRef, useMemo, useEffect, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaTimes, FaFileInvoice, FaFileExport } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { months_list } from '../../Shared/List';
import classnames from 'classnames';
import axios from 'axios';
import Server from "../../Shared/Server";
import { API_BASE_URL } from '../../Shared/Server';
import AuthContext from '../../../context/auth/AuthContext';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const emptyCellFormatter = (params) =>
  params.value === null || params.value === undefined || params.value === '' ? '-' : params.value;

const ServerSideBilled = () => {
  const [show, setShow] = useState(false);
  const [publicID, setPublicID] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({ year: 0, month: 0 });
  const gridApi = useRef();
  const gridRef = useRef();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { data } = location.state;

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      year: data.year,
      month: data.month
    }));
  }, [data.year, data.month]);

  const title = `${months_list[data.month - 1].name} - ${data.year} All Billed Customers`;

  const columnDefs = useMemo(() => [
    {
      headerName: "#",
      filter: false,
      sortable: false,
      maxWidth: 70,
      valueFormatter: (params) => (params.node.rowIndex + 1).toString(),
    },
    {
      headerName: "Actions",
      minWidth: 140,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: (e) => {
        const row = e.data;
        if (!row) return null;
        if (e.node.rowPinned) return '';
        const payload = {
          year: data.year,
          month: data.month,
          contact_number: row.contact_number,
        };
        return (
          <div className="d-flex gap-1">
            <button
              className={classnames('btn btn-outline-danger', { disabled: row.is_paid === true })}
              onClick={() => row.is_paid !== true && handleShow(row.public_id)}
              title={row.is_paid ? "Cannot cancel (paid)" : "Cancel"}
            >
              <FaTimes />
            </button>
            <Link
              className="btn btn-outline-success"
              to="/invoice"
              state={{ payload }}
              title="Invoice"
            >
              <FaFileInvoice />
            </Link>
          </div>
        );
      },
    },
    { headerName: "Date", field: "date", minWidth: 120 },
    { headerName: "PID", field: "previous_id", minWidth: 110 },
    { headerName: "ID", field: "location_id", minWidth: 110 },
    { headerName: "Name", field: "full_name", minWidth: 220 },
    { headerName: "Remark", field: "remark" },
    { headerName: "Contact", field: "contact_number" },
    { headerName: "Prev.Reading", field: "previous_reading", filter: 'agNumberColumnFilter' },
    { headerName: "Curr.Reading", field: "current_reading", filter: 'agNumberColumnFilter' },
    {
      headerName: "Difference",
      field: "difference",
      filter: 'agNumberColumnFilter',
      valueFormatter: params => (params.value === null || params.value === undefined || params.value === '') ? '-' : Number(params.value),
      comparator: (valueA, valueB) => valueA - valueB
    },
    { headerName: "Prev.Balance", field: "previous_balance", filter: 'agNumberColumnFilter' },
    { headerName: "Debit", field: "debit", filter: 'agNumberColumnFilter' },
    { headerName: "Total", field: "total", filter: 'agNumberColumnFilter' },
    { headerName: "Meter ID", field: "serial_number" },
    { headerName: "Lock ID", field: "lock_number" },
    { headerName: "Rate", field: "rate_name" },
    { headerName: "Village", field: "village_name" },
    { headerName: "Zone", field: "zone_name" },
    { headerName: "Station", field: "station_name" },
    { headerName: "Line", field: "line_name" },
  ], [data.year, data.month]);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: true,
    resizable: true,
    valueFormatter: emptyCellFormatter,
    filterParams: {
      buttons: []
    }
  }), []);

  const fetchData = async (params) => {
    const dataColumns = columnDefs.filter(col => col.field);
    const requestParams = {
      draw: 1,
      start: params.startRow,
      length: params.endRow - params.startRow,
      year: data.year,
      month: data.month,
      columns: dataColumns.map(col => ({
        data: col.field,
        searchable: col.filter !== false,
        orderable: !!col.sortable,
        search: { value: '', regex: false }
      })),
      order: params.sortModel.map(sort => ({
        column: dataColumns.findIndex(col => col.field === sort.colId),
        dir: sort.sort
      })),
      search: Object.keys(params.filterModel).map(key => ({
        col: key,
        value: params.filterModel[key].filter?.toString().toLowerCase() ?? '',
        type: params.filterModel[key].type || "contains"
      })),
      filter: {}
    };

    try {
      const response = await Server.post('/billed/serverside', requestParams);
      params.successCallback(response.data.data, response.data.recordsFiltered);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load billed');
      params.failCallback();
    }
  };

  const onGridReady = (params) => {
    gridApi.current = params.api;
    params.api.updateGridOptions({
      datasource: {
        getRows: (p) => fetchData(p)
      }
    });
  };

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  };

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  };

  const handleClose = () => {
    if (isDeleting) return;
    setShow(false);
  };

  const handleShow = (public_id) => {
    setShow(true);
    setPublicID(public_id);
  };

  const handleDelete = async (public_id) => {
    setIsDeleting(true);
    try {
      const response = await Server.delete(`/billed/${public_id}`);
      toast.success(response.data.detail);
      gridApi.current?.purgeInfiniteCache();
      setShow(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate('/login');
      } else if (error?.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to cancel bill');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/billed/download?year=${formData.year}&month=${formData.month}`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${user.access_token}`,
            'Content-Type': 'application/json',
            'API-Key': process.env.REACT_APP_API_KEY
          }
        }
      );

      let filename = `Billed-${formData.year}-${formData.month}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+\.xlsx)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      if (blob.size < 100) {
        const errorText = await blob.text();
        throw new Error(`Small file received: ${errorText.substring(0, 100)}`);
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const errorText = await err.response.data.text();
        const errorJson = JSON.parse(errorText);
        toast.error(errorJson.detail || 'Download failed');
      } else {
        toast.error('Download failed');
      }
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <button className="btn btn-outline-info mb-2 float-end" onClick={handleDownload}>
            <FaFileExport /> &nbsp;Download
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">{title}</h3>
        </div>
        <div className="card-body">
          <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowModelType="infinite"
              cacheBlockSize={20}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              enableCellTextSelection={true}
              suppressCellFocus={true}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              onGridSizeChanged={onGridSizeChanged}
            />
          </div>
        </div>
      </div>

      <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Cancel Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel this bill? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
            {isDeleting ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br />
    </>
  );
};

export default ServerSideBilled;
